import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Ban, ArrowLeft, Globe2, Gauge, MapPin, Activity, Lock, Search, MousePointerClick, Database } from 'lucide-react';

export default function AdminCountryFares() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [role, setRole] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => { document.title = 'Admin · Country Fares | FlightDeals.in'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => { if (!user) return; api.roleMe().then(({ data }) => setRole(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }); }, [user]);

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-lg mx-auto py-24 text-center"><Ban className="h-10 w-10 mx-auto mb-2 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Staff access required</h1></div></Layout>;
  const canAdmin = role && role.role !== 'editor';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/admin')} className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1 mb-3" data-testid="cf-back"><ArrowLeft className="h-4 w-4" /> Back to dashboard</button>
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Globe2 className="h-6 w-6 text-accent" /> Country Fare Explorer</h1>
          {role && <Badge className="rounded-full bg-accent/15 text-accent">{role.role.replace('_', ' ')}</Badge>}
        </div>
        <p className="text-sm text-muted-foreground mb-6">Configure candidate fan-out, ranking, caching and the geographic model. Isolated from normal flight search.</p>

        <Tabs defaultValue="monitor" data-testid="cf-tabs">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="monitor" data-testid="cf-tab-monitor"><Activity className="h-4 w-4 mr-1.5" />Monitoring</TabsTrigger>
            <TabsTrigger value="settings" data-testid="cf-tab-settings"><Gauge className="h-4 w-4 mr-1.5" />Settings</TabsTrigger>
            <TabsTrigger value="geo" data-testid="cf-tab-geo"><MapPin className="h-4 w-4 mr-1.5" />Geography</TabsTrigger>
          </TabsList>
          <TabsContent value="monitor" className="mt-4"><Monitor /></TabsContent>
          <TabsContent value="settings" className="mt-4"><Settings canAdmin={canAdmin} /></TabsContent>
          <TabsContent value="geo" className="mt-4"><Geo /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

const KPI = ({ icon: Icon, label, value }) => (
  <Card className="p-4 rounded-2xl"><div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Icon className="h-4 w-4 text-accent" />{label}</div><p className="text-2xl font-semibold tabular">{value}</p></Card>
);

function Monitor() {
  const [d, setD] = useState(null);
  useEffect(() => { api.cfDashboard().then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="grid gap-3 grid-cols-2 md:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  const m = d.metrics;
  return (
    <div className="space-y-6" data-testid="cf-monitor">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <KPI icon={Search} label="Country searches" value={m.searches} />
        <KPI icon={MousePointerClick} label="Route clicks" value={m.clicks} />
        <KPI icon={Activity} label="Drill-downs" value={m.drilldowns} />
        <KPI icon={Database} label="Cache hit rate" value={`${m.ctr}%`} />
        <KPI icon={Database} label="Cached entries" value={m.cached_entries} />
        <KPI icon={Activity} label="Impressions" value={m.impressions} />
        <KPI icon={Database} label="Sessions" value={m.sessions} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5 rounded-2xl"><h3 className="font-semibold text-sm mb-3">Most searched country pairs</h3>
          {d.top_pairs.length === 0 ? <p className="text-sm text-muted-foreground">No searches yet.</p> : d.top_pairs.map((p) => (
            <div key={p.pair} className="flex justify-between text-sm border-b border-border last:border-0 py-1.5"><span className="font-medium">{p.pair}</span><span className="tabular">{p.count}</span></div>
          ))}
        </Card>
        <Card className="p-5 rounded-2xl"><h3 className="font-semibold text-sm mb-3">Recent cheapest routes</h3>
          {d.recent_cheapest.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : d.recent_cheapest.map((r, i) => (
            <div key={i} className="flex justify-between text-sm border-b border-border last:border-0 py-1.5"><span className="font-medium">{r.route} <span className="text-xs text-muted-foreground">({r.pair})</span></span><span className="tabular text-accent">{formatPrice(r.price, r.currency)}</span></div>
          ))}
        </Card>
      </div>
    </div>
  );
}

const NUMFIELDS = [
  ['max_candidate_origins', 'Max candidate origins'], ['max_candidate_destinations', 'Max candidate destinations'],
  ['max_provider_calls', 'Max provider calls'], ['concurrency', 'Concurrency'],
  ['provider_timeout_seconds', 'Provider timeout (s)'], ['cache_ttl_minutes', 'Cache TTL (min)'],
  ['min_result_count', 'Min result count'], ['explore_reference_days', 'Explore reference days'],
];
function Settings({ canAdmin }) {
  const [s, setS] = useState(null);
  const load = () => api.cfSettings().then(({ data }) => setS(data.settings)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return <Skeleton className="h-64 rounded-2xl" />;
  const num = (k, v) => setS({ ...s, [k]: v === '' ? '' : Number(v) });
  const w = (k, v) => setS({ ...s, ranking_weights: { ...s.ranking_weights, [k]: Number(v) || 0 } });
  const save = async () => { try { await api.cfUpdateSettings(s); toast.success('Settings saved'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Only admins can change settings'); } };
  return (
    <div className="space-y-5" data-testid="cf-settings">
      {!canAdmin && <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 flex items-center gap-2"><Lock className="h-4 w-4" /> Read-only: editors cannot change Country Fare settings.</div>}
      <Card className="p-5 rounded-2xl space-y-4">
        <div className="flex items-center justify-between"><h3 className="font-semibold text-sm">Fan-out & performance</h3>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.enabled} disabled={!canAdmin} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="cf-set-enabled" />Enabled</label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {NUMFIELDS.map(([k, l]) => (<div key={k}><Label className="text-xs">{l}</Label><Input type="number" value={s[k] ?? ''} disabled={!canAdmin} onChange={(e) => num(k, e.target.value)} data-testid={`cf-set-${k}`} /></div>))}
        </div>
      </Card>
      <Card className="p-5 rounded-2xl space-y-3">
        <h3 className="font-semibold text-sm">Ranking weights (actual price stays primary)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['price', 'direct', 'duration', 'confidence'].map((k) => (<div key={k}><Label className="text-xs capitalize">{k}</Label><Input type="number" value={s.ranking_weights?.[k] ?? ''} disabled={!canAdmin} onChange={(e) => w(k, e.target.value)} data-testid={`cf-weight-${k}`} /></div>))}
        </div>
        <p className="text-[11px] text-muted-foreground">Sponsorship/merchandising never changes country fare ranking or actual price.</p>
      </Card>
      {canAdmin && <Button onClick={save} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="cf-settings-save">Save settings</Button>}
    </div>
  );
}

function Geo() {
  const [country, setCountry] = useState('IN');
  const [items, setItems] = useState(null);
  const [countries, setCountries] = useState([]);
  useEffect(() => { api.countryMeta().then(({ data }) => setCountries(data.countries || [])).catch(() => {}); }, []);
  const load = () => api.cfGeo(country).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [country]);
  const toggleExcl = async (code, excluded) => { try { await api.cfPatchAirport(code, { excluded }); toast.success(excluded ? 'Excluded' : 'Included'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  return (
    <div className="space-y-4" data-testid="cf-geo">
      <div className="flex flex-wrap gap-2">
        {countries.map((c) => (
          <button key={c.country_code} onClick={() => setCountry(c.country_code)} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${country === c.country_code ? 'bg-accent text-accent-foreground border-accent' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`} data-testid={`cf-geo-country-${c.country_code}`}>{c.flag} {c.name}</button>
        ))}
      </div>
      {!items ? <Skeleton className="h-40 rounded-2xl" /> : (
        <Card className="p-0 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 px-4">Airport</th><th>City / Region</th><th>Importance</th><th className="px-4">Status</th></tr></thead>
            <tbody>{items.map((a) => (
              <tr key={a.code} className="border-b border-border last:border-0" data-testid={`cf-geo-airport-${a.code}`}>
                <td className="py-2.5 px-4"><span className="font-mono font-semibold">{a.code}</span> <span className="text-xs text-muted-foreground">{a.airport_name}</span></td>
                <td className="text-xs">{a.city} · {a.region}</td>
                <td className="tabular">{a.importance}</td>
                <td className="px-4"><label className="flex items-center gap-2 text-xs"><Switch checked={!a.excluded} onCheckedChange={(v) => toggleExcl(a.code, !v)} data-testid={`cf-geo-toggle-${a.code}`} />{a.excluded ? 'Excluded' : 'Active'}</label></td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
